// AI reading (PRD F10) of uploaded contracts with an open model on Workers AI.
//
//   1. Classify: the first pages and the list of contract types → the type.
//   2. Extract: a form of that type's terms (plus terms common to any contract), each a named
//      field the model fills or leaves null, plus up to 10 other notable points.
//
// A quick scan reads the first pages and the sections most likely to hold those terms. A full
// scan reads the whole document in windows and merges the answers. The model only suggests:
// everything is cleaned here and nothing is saved until the user confirms the form.
import { FIELDS, TYPES, customKey, fieldFor, termsForType, typeFor } from "./catalog";
import { LIMITS, RENEWAL_TYPES, detailProblem, normalizeDetailValue, type ContractFields, type Details, type Party, type RenewalType } from "./contract";
import { isValidIsoDate } from "./dates";
import { currencyDigits, isKnownCurrency, parseMoneyInput } from "./money";

/** Bumped whenever the prompts or the clean-up change, so cached answers aren't reused. */
export const PROMPT_VERSION = 2;
export const DEFAULT_AI_MODEL = "@cf/meta/llama-3.3-70b-instruct-fp8-fast";
export const MAX_INPUT_TEXT = 500_000;
/** Characters of the document a quick scan reads. */
export const QUICK_BUDGET = 20_000;
/** The first pages always read: the schedule, parties and key dates usually live there. */
export const QUICK_HEAD = 6_000;
/** Characters per full-scan window; with the prompt and answer it fits the model's context. */
export const WINDOW = 40_000;
/** Characters the type is decided from. */
export const CLASSIFY_CHARS = 5_000;
/** At most this many "other points" a read may add as custom terms. */
export const MAX_OTHER_POINTS = 10;

export type ScanMode = "quick" | "full";

export interface AiSuggestions {
  type?: string;
  title?: string;
  parties?: Party[];
  startDate?: string;
  endDate?: string;
  noticePeriodDays?: number;
  renewalType?: RenewalType;
  renewalTermMonths?: number;
  value?: { minor: number; currency: string };
  details?: Details;
}

type Message = { role: "system" | "user"; content: string };

/** NUL bytes out, runs of spaces squeezed, at most one blank line between paragraphs. */
export function cleanText(text: string): string {
  return text.replaceAll("\0", "").replace(/[ \t]+/g, " ").replace(/ *\n */g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

// --- picking what to read -------------------------------------------------------------

const fold = (s: string) => s.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "");
const STOP = new Set(["with", "from", "that", "this", "your", "their", "days", "months", "years", "date", "amount", "number", "paid", "period"]);

/** Words and phrases that suggest a passage talks about one of these terms. */
export function keywordsFor(terms: readonly string[]): string[] {
  const out = new Set<string>();
  for (const key of terms) {
    const f = fieldFor(key);
    if (!f) continue;
    const label = fold(f.label.replace(/\(.*?\)/g, "")).replace(/[^a-z0-9 -]+/g, " ").trim();
    if (label.length >= 4) out.add(label);
    for (const w of [...label.split(" "), ...fold(f.aka ?? "").split(/[^a-z0-9-]+/)]) if (w.length >= 4 && !STOP.has(w)) out.add(w);
  }
  return [...out];
}

/** Splits text into paragraph-aligned sections of roughly `size` characters. */
export function sections(text: string, size: number): { start: number; text: string }[] {
  const out: { start: number; text: string }[] = [];
  let start = 0;
  while (start < text.length) {
    let end = Math.min(text.length, start + size);
    if (end < text.length) {
      // End at a paragraph or line break if there's one in the last quarter of the section.
      const para = text.lastIndexOf("\n\n", end);
      const line = text.lastIndexOf("\n", end);
      const cut = para > start + size * 0.75 ? para : line > start + size * 0.75 ? line : end;
      end = cut;
    }
    out.push({ start, text: text.slice(start, end).trim() });
    start = end;
  }
  return out.filter((s) => s.text);
}

/**
 * A quick scan's reading: the first pages, then the sections that mention the most of this
 * type's terms, in document order, within QUICK_BUDGET characters.
 */
export function quickPassage(text: string, type: string | null | undefined): { passage: string; partial: boolean; readChars: number } {
  const clean = cleanText(text);
  if (clean.length <= QUICK_BUDGET) return { passage: clean, partial: false, readChars: clean.length };
  const head = sections(clean, QUICK_HEAD)[0]!;
  const words = keywordsFor(termsForType(type));
  const rest = sections(clean.slice(head.text.length), 1_500).map((s) => ({ ...s, start: s.start + head.text.length }));
  const scored = rest
    .map((s) => {
      const t = fold(s.text);
      return { ...s, score: words.reduce((n, w) => n + (t.includes(w) ? (w.includes(" ") ? 3 : 1) : 0), 0) };
    })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score || a.start - b.start);
  const picked: typeof rest = [];
  let used = head.text.length;
  for (const s of scored) {
    if (used + s.text.length > QUICK_BUDGET) continue;
    picked.push(s);
    used += s.text.length;
  }
  picked.sort((a, b) => a.start - b.start);
  const passage = [head.text, ...picked.map((s) => s.text)].join("\n\n[…]\n\n");
  return { passage, partial: true, readChars: used };
}

/** A full scan's windows: the whole document, in order. */
export function fullWindows(text: string): string[] {
  return sections(cleanText(text), WINDOW).map((s) => s.text);
}

// --- prompts --------------------------------------------------------------------------

export function classifyMessages(text: string): Message[] {
  return [
    {
      role: "system",
      content: `You identify what kind of contract or document this is. Reply with JSON: {"type": one id from the list or null, "title": the document's own title or null}.

Contract types:
${TYPES.map((t) => `${t.id}: ${t.label}`).join("\n")}`,
    },
    { role: "user", content: cleanText(text).slice(0, CLASSIFY_CHARS) },
  ];
}

export const CLASSIFY_SCHEMA = {
  type: "object",
  properties: { type: { type: ["string", "null"] }, title: { type: ["string", "null"] } },
  required: ["type"],
} as const;

const describe = (key: string) => {
  const f = fieldFor(key)!;
  const kind =
    f.kind === "select"
      ? `one of: ${f.options!.join(" | ")}`
      : { text: "text", longtext: "text (short summary)", date: "date YYYY-MM-DD", number: "number", money: "amount, digits only", percent: "percentage, digits only", yesno: '"yes" or "no"' }[f.kind];
  return `${f.label} (${kind})`;
};

/** The form the model fills for a contract of this type: every term a named field. */
export function extractSchema(type: string | null | undefined) {
  const terms = termsForType(type);
  return {
    type: "object",
    properties: {
      parties: {
        type: "array",
        items: { type: "object", properties: { name: { type: "string" }, role: { type: "string" } }, required: ["name", "role"] },
      },
      startDate: { type: ["string", "null"], description: "Start or commencement date, YYYY-MM-DD" },
      endDate: { type: ["string", "null"], description: "End or expiry of the current term, YYYY-MM-DD" },
      noticePeriodDays: { type: ["number", "null"], description: "Days of notice needed to end or not renew" },
      renewalType: { type: ["string", "null"], enum: [...RENEWAL_TYPES, null] },
      renewalTermMonths: { type: ["number", "null"] },
      value: {
        type: ["object", "null"],
        description: "Total contract value",
        properties: { amount: { type: ["number", "string"] }, currency: { type: "string", description: "ISO code, e.g. INR" } },
      },
      terms: {
        type: "object",
        properties: Object.fromEntries(terms.map((k) => [k, { type: ["string", "null"], description: describe(k) }])),
      },
      other_points: {
        type: "array",
        maxItems: MAX_OTHER_POINTS,
        description: "Other important facts not covered by the terms above",
        items: { type: "object", properties: { label: { type: "string" }, value: { type: "string" } }, required: ["label", "value"] },
      },
    },
    required: ["parties", "terms"],
  };
}

export function extractMessages(type: string | null | undefined, passage: string, part?: { index: number; total: number }): Message[] {
  const t = typeFor(type);
  const roles = t ? t.roles.join(", ") : "the parties, guarantors, witnesses";
  return [
    {
      role: "system",
      content: `You read ${t ? `a ${t.label.toLowerCase()}` : "a contract"} and fill in a form about it as JSON. Use only what the document states; set a field to null when it isn't stated. Never invent names, dates or amounts.

- "parties": everyone named with a role in it. Use these roles where they fit: ${roles}. Include people in tables (for example a nominee or an insured member).
- Dates as YYYY-MM-DD. "endDate" is when the current term ends.
- "terms": fill every field you can find. Numbers without commas or currency symbols; yes/no fields as "yes" or "no"; choice fields exactly as one of their options; long text as a short summary.
- "other_points": up to ${MAX_OTHER_POINTS} other facts that matter to someone managing this contract and that no term above covers, each as a short label and value.`,
    },
    {
      role: "user",
      content: `${part && part.total > 1 ? `This is part ${part.index + 1} of ${part.total} of the document.\n\n` : ""}${passage}`,
    },
  ];
}

/** Characters of everything sent in an extract call, for cost estimates. */
export const extractPromptChars = (type: string | null | undefined, passage: string) =>
  extractMessages(type, passage).reduce((n, m) => n + m.content.length, 0) + JSON.stringify(extractSchema(type)).length;
export const classifyPromptChars = (text: string) => classifyMessages(text).reduce((n, m) => n + m.content.length, 0) + JSON.stringify(CLASSIFY_SCHEMA).length;

// --- cleaning the answers -------------------------------------------------------------

const str = (v: unknown, max: number): string | undefined => {
  if (typeof v !== "string") return undefined;
  const s = v.replaceAll("\0", "").trim().replace(/\s+/g, " ");
  return s && s.length <= max && !/^(null|n\/?a|none|not (stated|mentioned|specified))$/i.test(s) ? s : undefined;
};
const int = (v: unknown, min: number, max: number): number | undefined => {
  const n = typeof v === "string" ? Number(v.trim()) : v;
  return typeof n === "number" && Number.isInteger(n) && n >= min && n <= max ? n : undefined;
};
const date = (v: unknown): string | undefined => (typeof v === "string" && isValidIsoDate(v.trim()) ? v.trim() : undefined);

function asObject(raw: unknown): Record<string, unknown> | null {
  let r = raw;
  if (typeof r === "string") {
    const m = /\{[\s\S]*\}/.exec(r);
    try {
      r = m ? JSON.parse(m[0]) : null;
    } catch {
      r = null;
    }
  }
  return r && typeof r === "object" && !Array.isArray(r) ? (r as Record<string, unknown>) : null;
}

export function sanitizeClassification(raw: unknown): { type?: string; title?: string } {
  const o = asObject(raw);
  if (!o) return {};
  const out: { type?: string; title?: string } = {};
  if (typeof o.type === "string" && TYPES.some((t) => t.id === o.type)) out.type = o.type;
  const title = str(o.title, LIMITS.title);
  if (title) out.title = title;
  return out;
}

const labelToKey = new Map(FIELDS.map((f) => [fold(f.label), f.key]));
const CUSTOM_LABEL_MAX = 40;
const CUSTOM_VALUE_MAX = 300;

/**
 * Turns one extract answer (an object, or JSON text, possibly in a code fence) into suggestions
 * that are all valid: catalogue types and terms only, values valid for their kind, and at most
 * MAX_OTHER_POINTS short custom terms from "other_points".
 */
export function sanitizeAiResult(raw: unknown): AiSuggestions {
  const o = asObject(raw);
  if (!o) return {};
  const out: AiSuggestions = {};

  if (typeof o.type === "string" && TYPES.some((t) => t.id === o.type)) out.type = o.type;
  const title = str(o.title, LIMITS.title);
  if (title) out.title = title;

  if (Array.isArray(o.parties)) {
    const seen = new Set<string>();
    const parties: Party[] = [];
    for (const p of o.parties.slice(0, LIMITS.parties * 2)) {
      if (!p || typeof p !== "object" || parties.length >= LIMITS.parties) continue;
      const name = str((p as Record<string, unknown>).name, LIMITS.partyName);
      if (!name || seen.has(name.toLowerCase())) continue;
      seen.add(name.toLowerCase());
      parties.push({ name, role: str((p as Record<string, unknown>).role, LIMITS.partyRole) ?? "", email: null, phone: null });
    }
    if (parties.length) out.parties = parties;
  }

  const start = date(o.startDate);
  const end = date(o.endDate);
  if (start) out.startDate = start;
  if (end && (!start || end >= start)) out.endDate = end;
  const notice = int(o.noticePeriodDays, 0, LIMITS.noticeDays);
  if (notice !== undefined) out.noticePeriodDays = notice;
  if (typeof o.renewalType === "string" && (RENEWAL_TYPES as readonly string[]).includes(o.renewalType)) out.renewalType = o.renewalType as RenewalType;
  const term = int(o.renewalTermMonths, 1, LIMITS.termMonths);
  if (term !== undefined && out.renewalType !== "none") out.renewalTermMonths = term;

  if (o.value && typeof o.value === "object") {
    const v = o.value as Record<string, unknown>;
    const currency = typeof v.currency === "string" ? v.currency.trim().toUpperCase() : "";
    const amount = typeof v.amount === "number" ? String(v.amount) : typeof v.amount === "string" ? v.amount : "";
    if (isKnownCurrency(currency) && amount) {
      const parsed = parseMoneyInput(amount, currency);
      if (parsed.ok && parsed.minor !== null && parsed.minor > 0) out.value = { minor: parsed.minor, currency };
    }
  }

  const details: Details = {};
  const add = (key: string, rawValue: unknown) => {
    if (details[key] || Object.keys(details).length >= LIMITS.details) return;
    const value = normalizeDetailValue(key, rawValue);
    if (value && !/^(null|n\/?a|none|not (stated|mentioned|specified))$/i.test(value) && !detailProblem(key, value)) details[key] = value;
  };
  if (o.terms && typeof o.terms === "object" && !Array.isArray(o.terms)) {
    for (const [key, rawValue] of Object.entries(o.terms as Record<string, unknown>)) {
      // Only catalogue terms here; the model's own extras arrive as other_points.
      if (fieldFor(key)) add(key, rawValue);
    }
  }
  if (Array.isArray(o.other_points)) {
    let added = 0;
    for (const p of o.other_points) {
      if (added >= MAX_OTHER_POINTS || !p || typeof p !== "object") continue;
      const label = str((p as Record<string, unknown>).label, CUSTOM_LABEL_MAX);
      const value = str((p as Record<string, unknown>).value, CUSTOM_VALUE_MAX);
      if (!label || !value) continue;
      // A point that is really a catalogue term goes there instead.
      const known = labelToKey.get(fold(label));
      const key = known ?? customKey(label);
      const before = Object.keys(details).length;
      add(key, value);
      if (Object.keys(details).length > before) added++;
    }
  }
  if (Object.keys(details).length) out.details = details;
  return out;
}

/**
 * Combines the answers for several windows of one document. Earlier windows win for single
 * values (the schedule and first pages are usually most reliable); people and terms are pooled.
 */
export function mergeResults(results: readonly AiSuggestions[]): AiSuggestions {
  const out: AiSuggestions = {};
  const parties: Party[] = [];
  const details: Details = {};
  let custom = 0;
  for (const r of results) {
    for (const k of ["type", "title", "startDate", "endDate", "noticePeriodDays", "renewalType", "renewalTermMonths", "value"] as const) {
      if (out[k] === undefined && r[k] !== undefined) (out as Record<string, unknown>)[k] = r[k];
    }
    for (const p of r.parties ?? []) {
      if (parties.length < LIMITS.parties && !parties.some((x) => x.name.toLowerCase() === p.name.toLowerCase())) parties.push(p);
    }
    for (const [k, v] of Object.entries(r.details ?? {})) {
      if (details[k] || Object.keys(details).length >= LIMITS.details) continue;
      if (k.startsWith("x:")) {
        if (custom >= MAX_OTHER_POINTS) continue;
        custom++;
      }
      details[k] = v;
    }
  }
  if (parties.length) out.parties = parties;
  if (Object.keys(details).length) out.details = details;
  return out;
}

/**
 * Fills the form from AI suggestions without overwriting anything already there (typed by the
 * user or found by the rule-based extractor, which quotes its evidence). Returns the new fields
 * and the terms that came from AI, so the form can label them.
 */
export function mergeAiSuggestions(prev: ContractFields, ai: AiSuggestions): { fields: ContractFields; aiKeys: Set<string>; filled: number } {
  const fields = { ...prev, parties: [...prev.parties], details: { ...prev.details } };
  const aiKeys = new Set<string>();
  let filled = 0;
  const take = <K extends keyof ContractFields>(key: K, value: ContractFields[K] | undefined, empty: boolean) => {
    if (value !== undefined && empty) {
      fields[key] = value;
      filled++;
    }
  };
  take("type", ai.type, !prev.type);
  take("startDate", ai.startDate, !prev.startDate);
  take("endDate", ai.endDate, !prev.endDate);
  take("noticePeriodDays", ai.noticePeriodDays, prev.noticePeriodDays === null);
  if (prev.renewalType === "none" && ai.renewalType && ai.renewalType !== "none") {
    fields.renewalType = ai.renewalType;
    filled++;
  }
  take("renewalTermMonths", ai.renewalTermMonths, prev.renewalTermMonths === null && fields.renewalType !== "none");
  if (ai.value && !fields.details.contract_value && prev.valueMinor === null) {
    // The total value is a term now; amounts use the contract's currency.
    const digits = currencyDigits(ai.value.currency);
    fields.details.contract_value = (ai.value.minor / 10 ** digits).toFixed(digits).replace(/\.0+$/, "");
    if (!fields.currency) fields.currency = ai.value.currency;
    aiKeys.add("contract_value");
    filled++;
  }
  for (const p of ai.parties ?? []) {
    if (fields.parties.some((x) => x.name.toLowerCase() === p.name.toLowerCase())) continue;
    fields.parties.push(p);
    filled++;
  }
  for (const [k, v] of Object.entries(ai.details ?? {})) {
    if (fields.details[k]) continue;
    fields.details[k] = v;
    aiKeys.add(k);
    filled++;
  }
  return { fields, aiKeys, filled };
}
